import React, { useState, useMemo } from 'react';
import { Icons } from '../ui/Icons';
import Modal from '../ui/Modal';
import { formatDateYYYYMMDD } from '../../utils/dateUtils';

interface DateRangeSelectionModalProps {
    initialStartDate: string | null;
    initialEndDate: string | null;
    onClose: () => void;
    onConfirm: (startDate: string, endDate: string) => void;
}

const DateRangeSelectionModal: React.FC<DateRangeSelectionModalProps> = ({ initialStartDate, initialEndDate, onClose, onConfirm }) => {
    const parseDate = (dateString: string | null): Date | null => {
        if (!dateString) return null;
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day);
    };
    
    const [currentMonth, setCurrentMonth] = useState(parseDate(initialStartDate) || new Date());
    const [startDate, setStartDate] = useState<Date | null>(parseDate(initialStartDate));
    const [endDate, setEndDate] = useState<Date | null>(parseDate(initialEndDate));
    const [selecting, setSelecting] = useState<'start' | 'end'>('start');
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const handleMonthChange = (offset: number) => {
        setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + offset, 1));
    };
    
    const handleDayClick = (day: Date) => {
        if (day < today) return; // Don't allow selecting past dates
        
        if (selecting === 'start' || !startDate || day < startDate) {
            setStartDate(day);
            setEndDate(null);
            setSelecting('end');
        } else {
            setEndDate(day);
            setSelecting('start');
        }
    };
    
    const handleConfirm = () => {
        if (startDate && endDate) {
            onConfirm(formatDateYYYYMMDD(startDate), formatDateYYYYMMDD(endDate));
        } else if (startDate) {
            onConfirm(formatDateYYYYMMDD(startDate), formatDateYYYYMMDD(startDate));
        }
    };

    const calendarGrid = useMemo(() => {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        const firstDayOfMonth = new Date(year, month, 1);
        const lastDayOfMonth = new Date(year, month + 1, 0);

        const days = [];
        // Add days from previous month for padding
        const startDayOfWeek = firstDayOfMonth.getDay(); // 0 for Sunday
        const prefixDays = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1; // 0 for Monday
        for (let i = prefixDays; i > 0; i--) {
            const date = new Date(year, month, 1 - i);
            days.push({ date, isCurrentMonth: false });
        }

        // Add days of the current month
        for (let i = 1; i <= lastDayOfMonth.getDate(); i++) {
            const date = new Date(year, month, i);
            days.push({ date, isCurrentMonth: true });
        }

        // Add days from next month for padding
        const suffixDays = 42 - days.length; // 6 weeks * 7 days
        for (let i = 1; i <= suffixDays; i++) {
            const date = new Date(year, month + 1, i);
            days.push({ date, isCurrentMonth: false });
        }
        return days;
    }, [currentMonth]);

    const isSameDay = (d1: Date, d2: Date) => d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();

    return (
        <Modal title="Select Date Range" onClose={onClose}>
            <div className="p-4">
                <div className="flex items-center justify-between mb-4">
                    <button type="button" onClick={() => handleMonthChange(-1)} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700"><Icons.ChevronLeft className="w-5 h-5" /></button>
                    <div className="font-semibold text-gray-800 dark:text-gray-200">{currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' })}</div>
                    <button type="button" onClick={() => handleMonthChange(1)} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700"><Icons.ChevronRight className="w-5 h-5" /></button>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => <div key={day}>{day}</div>)}
                </div>
                <div className="grid grid-cols-7 gap-1">
                    {calendarGrid.map(({ date, isCurrentMonth }, index) => {
                        const isSelectedStart = startDate && isSameDay(date, startDate);
                        const isSelectedEnd = endDate && isSameDay(date, endDate);
                        const isInRange = startDate && endDate && date > startDate && date < endDate;
                        const isPast = date < today;
                        const isToday = isSameDay(date, today);

                        const baseClasses = "w-10 h-10 flex items-center justify-center rounded-full text-sm transition-colors";
                        let dayClasses = baseClasses;
                        if (!isCurrentMonth) {
                            dayClasses += " text-gray-300 dark:text-gray-600";
                        } else if (isPast) {
                            dayClasses += " text-gray-400 dark:text-gray-500 cursor-not-allowed line-through";
                        } else {
                            dayClasses += " text-gray-700 dark:text-gray-200 cursor-pointer";
                        }
                        
                        if (isSelectedStart || isSelectedEnd) {
                            dayClasses += " bg-indigo-600 text-white font-semibold";
                        } else if (isInRange) {
                            dayClasses += " bg-indigo-100 dark:bg-indigo-900/30";
                        } else if (!isPast) {
                             dayClasses += " hover:bg-gray-100 dark:hover:bg-slate-700";
                        }

                         if (isToday && !isSelectedStart && !isSelectedEnd) {
                            dayClasses += " ring-2 ring-indigo-500";
                        }

                        return (
                            <div key={index} className={`relative flex items-center justify-center ${isInRange || isSelectedEnd ? 'bg-indigo-100 dark:bg-indigo-900/30' : ''} ${isSelectedStart && endDate ? 'rounded-l-full' : ''} ${isSelectedEnd ? 'rounded-r-full' : ''}`}>
                                <button type="button" onClick={() => handleDayClick(date)} disabled={isPast && isCurrentMonth} className={dayClasses}>
                                    {date.getDate()}
                                </button>
                            </div>
                        )
                    })}
                </div>
            </div>
             <div className="bg-gray-50 dark:bg-slate-900/50 px-4 py-3 sm:px-6 flex justify-end gap-3 border-t dark:border-slate-700">
                <button
                    type="button"
                    onClick={onClose}
                    className="w-full sm:w-auto inline-flex justify-center rounded-md border border-gray-300 dark:border-slate-600 shadow-sm px-4 py-2 bg-white dark:bg-slate-700 text-base font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:text-sm"
                >
                    Cancel
                </button>
                 <button
                    type="button"
                    onClick={handleConfirm}
                    disabled={!startDate}
                    className="w-full sm:w-auto inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-slate-800 focus:ring-indigo-500 sm:text-sm disabled:bg-indigo-400 disabled:cursor-not-allowed"
                >
                    Confirm
                </button>
            </div>
        </Modal>
    );
};

export default DateRangeSelectionModal;
